import { Grid, Tooltip } from "@mui/material";
import { VFC } from "react";
import { useTranslation } from "react-i18next";
import { LoginAsButtonOutlined } from "../../../../components/Primitives";
import { useAuthContext } from "../../../../providers/Auth/auth.provider";
import { isAgencyGroup } from "../../../../domain/group/group-type";
import { getRolePomTranslation, RolePom } from "../../../../domain/user/role-pom";
import { isAdmin } from "../../../../domain/user/user";
import { getActiveStatusLabel } from "../../../../utils/translation.util";
import { AppDialog } from "../../../../components/dialog/app-dialog.component";
import { DetailValueDisplay } from "../../../../components/value-display/detail-value-display.component";
import { GroupDetailStatusTag } from "../group-detail-status-tag.component";

interface IUserDetailData {
  active: boolean;
  firstName: string;
  lastName: string;
  title: string;
  jobTitle?: string | null;
  email: string;
  deliveryEmail: string;
  telephone: string;
  rolePom?: RolePom;
  externalId: string;
  hasLogin?: boolean;
}

interface IUserDetailDialogProps {
  open: boolean;
  user: IUserDetailData | undefined;
  onClose: VoidFunction;
  onLoginAsClick?: VoidFunction;
  showJobTitleAndExternalId?: boolean;
}

export const UserDetailDialog: VFC<IUserDetailDialogProps> = (props) => {
  const { open, user, onClose, onLoginAsClick, showJobTitleAndExternalId = false } = props;
  const { t } = useTranslation();
  const { internalUser } = useAuthContext();

  const isLoginAsEnabled =
    (onLoginAsClick && isAdmin(internalUser)) ||
    isAgencyGroup(internalUser?.group?.type ? [internalUser.group.type] : []);
  const hasNoLogin = user?.hasLogin === false;

  return (
    <AppDialog
      title={t("basedata.users.detail")}
      open={open}
      onCancelClick={onClose}
      alignButtons="space-between"
    >
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Grid container direction="row" justifyContent="space-between" spacing={1}>
            <Grid item>
              <GroupDetailStatusTag
                label={getActiveStatusLabel(user?.active, t)}
                type={user?.active ? "success" : "error"}
              />
            </Grid>
            {isLoginAsEnabled && (
              <Grid item>
                <Tooltip title={hasNoLogin ? t("basedata.users.login.noLogin") : ""}>
                  {/* A disabled button fires no pointer events, so the tooltip anchors on the wrapper. */}
                  <span>
                    <LoginAsButtonOutlined variant="outlined" onClick={onLoginAsClick} disabled={hasNoLogin}>
                      {t("basedata.users.login.button", {
                        firstName: user?.firstName,
                        lastName: user?.lastName,
                      })}
                    </LoginAsButtonOutlined>
                  </span>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item>
          <Grid container direction="row">
            <Grid item xs={12} md={showJobTitleAndExternalId ? 6 : 12}>
              <DetailValueDisplay label={t("basedata.users.edit.title")} value={user?.title ?? "-"} />
            </Grid>
            {showJobTitleAndExternalId && (
              <Grid item xs={12} md={6}>
                <DetailValueDisplay label={t("basedata.users.edit.jobtitle")} value={user?.jobTitle ?? "-"} />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <DetailValueDisplay label={t("basedata.users.edit.firstName")} value={user?.firstName ?? "-"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailValueDisplay label={t("basedata.users.edit.lastName")} value={user?.lastName ?? "-"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailValueDisplay label={t("basedata.users.edit.loginEmail")} value={user?.email ?? "-"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailValueDisplay label={t("basedata.users.edit.deliveryEmail")} value={user?.deliveryEmail ?? "-"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailValueDisplay label={t("basedata.users.edit.phone")} value={user?.telephone ?? "-"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailValueDisplay
                label={t("basedata.users.edit.pom_type")}
                value={user?.rolePom ? t(getRolePomTranslation(user.rolePom)) : "-"}
              />
            </Grid>
            {showJobTitleAndExternalId && (
              <Grid item xs={12} md={12}>
                <DetailValueDisplay label={t("basedata.users.edit.externalId")} value={user?.externalId ?? "-"} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </AppDialog>
  );
};
